import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Calendar, Download, Mail, Phone, Home, Menu, X } from 'lucide-react';
import { calculateScore, generateInsights, determineNextSteps } from '../utils/quizScoring';
import axios from 'axios';
import '../styles/QuizResults.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuizResults = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState(null);

  const submitResultsToBackend = async (quizData, score, nextSteps) => {
    try {
      const payload = {
        name: quizData.contactInfo.name,
        email: quizData.contactInfo.email,
        phone: quizData.contactInfo.phone || '',
        location: quizData.contactInfo.location || '',
        score: score,
        category: nextSteps.category,
        pdfTitle: nextSteps.pdfTitle,
        answers: quizData.answers,
        variant: quizData.variant,
        timestamp: quizData.timestamp
      };

      await axios.post(`${BACKEND_URL}/api/submit-quiz`, payload);
      console.log('Quiz results submitted successfully');
    } catch (error) {
      console.error('Error submitting quiz results:', error);
      // Don't block user from seeing results if submission fails
    }
  };

  useEffect(() => {
    const storedResults = sessionStorage.getItem('quizResults');
    if (!storedResults) {
      navigate('/quiz-landing');
      return;
    }

    const quizData = JSON.parse(storedResults);
    const score = calculateScore(quizData.answers);
    const insights = generateInsights(quizData.answers, score);
    const nextSteps = determineNextSteps(quizData.answers, score);

    setResults({
      ...quizData,
      score,
      insights,
      nextSteps
    });

    // Submit to backend for Google Sheets tracking
    submitResultsToBackend(quizData, score, nextSteps);
  }, [navigate]);

  if (!results) {
    return (
      <div className="results-loading">
        <div className="spinner"></div>
        <p>Calculating your results...</p>
      </div>
    );
  }

  const getScoreColor = (score) => {
    if (score >= 66) return '#00FFD1';
    if (score >= 41) return '#FFD700';
    return '#FF6B6B';
  };

  const getScoreLabel = (score) => {
    if (score >= 66) return 'Strong Foundation';
    if (score >= 41) return 'Growth Potential';
    return 'Building the Basics';
  };

  return (
    <div className="results-container">
      <div className="results-header">
        <div className="quiz-logo-container">
          <img src="/logo.png" alt="LP Global Holdings" className="quiz-logo-img" />
        </div>
        <nav className="quiz-nav">
          <Link to="/" className="nav-home-link">
            <Home size={20} />
            Home
          </Link>
        </nav>
      </div>

      <div className="results-content">
        {/* Hero Results Section */}
        <section className="results-hero">
          <h1 className="results-title">Your Business Growth Assessment Results</h1>
          <p className="results-subtitle">Hello {results.contactInfo.name}, here's what we discovered about your business.</p>

          {/* Speedometer Score */}
          <div className="score-display">
            <div className="speedometer">
              <svg viewBox="0 0 200 120" className="speedometer-svg">
                {/* Background arc */}
                <path
                  d="M 20 100 A 80 80 0 0 1 180 100"
                  fill="none"
                  stroke="rgba(255, 255, 255, 0.1)"
                  strokeWidth="20"
                />
                {/* Colored arc based on score */}
                <path
                  d="M 20 100 A 80 80 0 0 1 180 100"
                  fill="none"
                  stroke={getScoreColor(results.score)}
                  strokeWidth="20"
                  strokeDasharray={`${(results.score / 100) * 251} 251`}
                  style={{ transition: 'stroke-dasharray 2s ease-out' }}
                />
                {/* Needle */}
                <line
                  x1="100"
                  y1="100"
                  x2={100 + 70 * Math.cos((Math.PI * (results.score / 100)))}
                  y2={100 - 70 * Math.sin((Math.PI * (results.score / 100)))}
                  stroke="white"
                  strokeWidth="3"
                  style={{ transition: 'all 2s ease-out' }}
                />
                <circle cx="100" cy="100" r="5" fill="white" />
              </svg>
              <div className="score-number">{results.score}%</div>
              <div className="score-label">{getScoreLabel(results.score)}</div>
            </div>
          </div>
        </section>

        {/* Insights Section */}
        <section className="insights-section">
          <h2 className="section-title">3 Key Insights About Your Business</h2>
          <div className="insights-grid">
            {results.insights.map((insight, idx) => (
              <div key={idx} className="insight-card">
                <div className="insight-number">{idx + 1}</div>
                <h3 className="insight-title">{insight.title}</h3>
                <p className="insight-description">{insight.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Next Steps Section */}
        <section className="nextsteps-section">
          <h2 className="section-title">Recommended Next Steps</h2>
          <div className="nextsteps-card">
            <div className="nextsteps-header">
              <h3 className="nextsteps-title">{results.nextSteps.title}</h3>
              <p className="nextsteps-description">{results.nextSteps.description}</p>
            </div>

            <div className="nextsteps-actions">
              {/* PDF Download - Primary CTA */}
              <div className="pdf-download-section">
                <div className="action-highlight">
                  <Download size={32} className="action-icon" />
                  <div>
                    <h4>Your FREE Business Guide</h4>
                    <p>{results.nextSteps.pdfTitle}</p>
                  </div>
                </div>
                <a 
                  href={results.nextSteps.pdfUrl} 
                  download={results.nextSteps.pdfTitle}
                  className="btn-primary cta-btn"
                >
                  <Download size={20} />
                  Download Your Free Guide
                </a>
              </div>

              {/* Secondary Actions */}
              {results.nextSteps.type === '1on1' && (
                <div className="secondary-action">
                  <div className="action-highlight">
                    <Calendar size={32} className="action-icon" />
                    <div>
                      <h4>Schedule a 1:1 Strategy Call</h4>
                      <p>Let's discuss how we can help transform your business.</p>
                    </div>
                  </div>
                  <a 
                    href="https://calendly.com/mikelucero968/30min" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary cta-btn"
                  >
                    <Calendar size={20} />
                    Book Your Call Now
                  </a>
                </div>
              )}

              {results.nextSteps.type === 'group' && (
                <div className="secondary-action">
                  <div className="action-highlight">
                    <Calendar size={32} className="action-icon" />
                    <div>
                      <h4>Join Our Group Workshop</h4>
                      <p>Learn proven systems to scale your business with other operators.</p>
                    </div>
                  </div>
                  <a 
                    href="https://calendly.com/mikelucero968/30min" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary cta-btn"
                  >
                    <Calendar size={20} />
                    Register for Workshop
                  </a>
                </div>
              )}

              <div className="additional-actions">
                <h4>Additional Resources:</h4>
                <ul className="resources-list">
                  {results.nextSteps.resources.map((resource, idx) => (
                    <li key={idx}>{resource}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="contact-section">
          <h2 className="section-title">Get in Touch</h2>
          <p className="section-subtitle">Have questions? Want to discuss your results? We're here to help.</p>
          
          <div className="contact-grid">
            <div className="contact-card">
              <Mail size={24} className="contact-icon" />
              <div>
                <div className="contact-label">Email</div>
                <a href="mailto:info@lpglobal.com" className="contact-value">info@lpglobal.com</a>
              </div>
            </div>
            <div className="contact-card">
              <Phone size={24} className="contact-icon" />
              <div>
                <div className="contact-label">Phone</div>
                <a href="tel:+15551234567" className="contact-value">(555) 123-4567</a>
              </div>
            </div>
          </div>

          {/* Mike's Profile */}
          <div className="mike-profile-section">
            <img src="/mike-photo.png" alt="Mike Lucero" className="mike-profile-photo" />
            <div className="mike-profile-info">
              <h4>Mike Lucero</h4>
              <p>Builder, Operator, Turnaround Specialist</p>
              <a href="https://instagram.com/michealaponsay" target="_blank" rel="noopener noreferrer" className="mike-instagram">
                @michealaponsay
              </a>
            </div>
          </div>

          <div className="personal-note">
            <p className="note-text">
              <strong>A note from Mike:</strong> Every business has unique challenges. These results are a starting point, but I'd love to hear your story directly. Whether you scored 20% or 80%, if you're committed to building something remarkable, let's talk.
            </p>
          </div>
        </section>
      </div>

      <footer className="results-footer">
        <p>© {new Date().getFullYear()} LP Global Holdings. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default QuizResults;
